import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { formatCurrency, formatDate, formatDateTime } from '../../utils/api'
import { api } from '../../utils/api'
import { SHOPIFY_ADMIN_URL } from '../../utils/constants'
import { FulfillmentBadge, FinancialBadge } from './StatusBadge'
import { ConsignmentBadge } from './ConsignmentBadge.jsx'
import PinButton from './PinButton'
import OrderNotes from './OrderNotes'
import OrderHoldControl from './OrderHoldControl'
import OrderTrackingEdit from './OrderTrackingEdit'
import OrderAddressEdit from './OrderAddressEdit'
import { holdReasonLabel } from '../../utils/orderHoldReasons'
import { generateVatInvoicePdf } from '../../utils/vatInvoice'
import { useOutboundPhotos } from '../../hooks/useOutboundPhotos'
import { OutboundPhotoPicker, OutboundPhotoList } from './OutboundPhotoPicker'

// A sku Prism issued: the Shopify product id, plus "-r<n>" when the listing
// was relisted. Same rule as parse_prism_sku in audiotrader-prism.
const PRISM_SKU = /^(\d+)(?:-r\d+)?$/

// The Shopify product id of an order line, or null when the line has none.
// Only a Shopify order carries it in product_id: on a Reverb order Prism puts
// the Reverb listing id there (since 2026-09-09), and eBay leaves it empty.
// A marketplace line carries it in its sku. A legacy sku (Listing-*, a tote
// code) is not a product id.
function shopifyProductRef(item, marketplace) {
  const isShopifyOrder = !marketplace || marketplace === 'shopify'
  if (isShopifyOrder && item.product_id) return String(item.product_id)
  return String(item.sku || '').match(PRISM_SKU)?.[1] || null
}

function OrderDetailModal({ order, onClose, onProductClick, loadingProduct, onOrderUpdate }) {
  const navigate = useNavigate()
  const [showTicketForm, setShowTicketForm] = useState(false)
  const [ticketSubject, setTicketSubject] = useState('')
  const [ticketMessage, setTicketMessage] = useState('')
  const [sendingTicket, setSendingTicket] = useState(false)
  const [ticketSent, setTicketSent] = useState(false)
  const ticketPhotos = useOutboundPhotos()
  const [fullOrder, setFullOrder] = useState(order)
  // The open hold, from the search row. The detail call does not carry it, so a
  // hold or release made here replaces it with what Prism answered.
  const [hold, setHold] = useState(order.on_hold ?? null)
  // Bumped after a hold, release, tracking edit or address edit so the notes load again and show the new entry.
  const [notesVersion, setNotesVersion] = useState(0)
  const [shipError, setShipError] = useState(null)
  const [loadingRefunds, setLoadingRefunds] = useState(false)
  const [showShipForm, setShowShipForm] = useState(false)
  const [shipping, setShipping] = useState(false)
  const [trackingNumber, setTrackingNumber] = useState('')
  const [shippingProvider, setShippingProvider] = useState('')
  const [carriers, setCarriers] = useState([])
  const [carriersError, setCarriersError] = useState(false)
  // Map<product_id_string, {shelf, ownership, split_pct}|null> for the line
  // items table. Populated once fullOrder.line_items resolves — per-item lookup
  // against item_location via the inventory-locations proxy, which also carries
  // the inventory-side ownership stamp (spec D7) for the consignment badge.
  const [itemLocations, setItemLocations] = useState({})

  // Re-fetch the canonical order detail (refunds, fulfillments/tracking, etc.)
  // and replace fullOrder. Used both on open and after shipping so the modal
  // reflects server truth instead of a partial optimistic update.
  const refreshOrderDetail = useCallback(async () => {
    if (!order?.marketplace || !order?.order_number) return
    const data = await api.get(`/api/orders/detail/${order.marketplace}/${order.order_number}`)
    if (data.order) setFullOrder({ ...data.order, printrun: data.printrun ?? null })
  }, [order?.marketplace, order?.order_number])

  // Fetch full order details (including refunds) when modal opens
  useEffect(() => {
    if (!order?.marketplace || !order?.order_number) return
    setLoadingRefunds(true)
    refreshOrderDetail()
      .catch(() => {}) // Silently fail — we still have the search result data
      .finally(() => setLoadingRefunds(false))
  }, [order?.marketplace, order?.order_number, refreshOrderDetail])

  // Fetch the canonical carrier list from Prism (via the Worker proxy).
  // Source of truth lives in Prism's app/services/orders/carriers.py;
  // display order is preserved, so list[0] is the preferred default.
  useEffect(() => {
    let cancelled = false
    api.get('/api/orders/carriers')
      .then(data => {
        if (cancelled) return
        const list = Array.isArray(data?.carriers) ? data.carriers : []
        if (list.length === 0) {
          setCarriers([])
          setCarriersError(true)
          return
        }
        setCarriers(list)
        setCarriersError(false)
        setShippingProvider(list[0])
      })
      .catch(() => {
        if (cancelled) return
        setCarriers([])
        setCarriersError(true)
      })
    return () => { cancelled = true }
  }, [])

  // Fan out shelf lookups for each unique line-item reference once the
  // full order loads, keyed by shopifyProductRef. The post-filter on
  // shopify_product_id keeps a search hit on another field from
  // counting as a match. Any 403 or 500 is swallowed.
  useEffect(() => {
    const refs = Array.from(new Set(
      (fullOrder?.line_items || [])
        .map(it => shopifyProductRef(it, order?.marketplace))
        .filter(Boolean)
    ))
    if (refs.length === 0) {
      setItemLocations({})
      return
    }
    let cancelled = false
    Promise.all(refs.map(ref =>
      api.get(`/api/inventory-locations/items?q=${encodeURIComponent(ref)}&limit=5`)
        .then(resp => {
          const items = Array.isArray(resp) ? resp : (resp?.items || [])
          const match = items.find(it => String(it.shopify_product_id) === ref)
          return [ref, match ? {
            shelf: match.current_shelf_id || null,
            ownership: match.ownership || null,
            split_pct: match.split_pct ?? null
          } : null]
        })
        .catch(() => [ref, null])
    )).then(pairs => {
      if (cancelled) return
      setItemLocations(Object.fromEntries(pairs))
    })
    return () => { cancelled = true }
  }, [fullOrder?.line_items, order?.marketplace])

  const formatDateDisplay = (dateStr) => {
    if (!dateStr) return '—'
    return new Date(dateStr).toLocaleString('en-GB', {
      dateStyle: 'medium',
      timeStyle: 'short'
    })
  }

  const isUnfulfilled = !order.fulfillment_status || order.fulfillment_status === 'unfulfilled'
  const isNotCancelled = !order.cancelled_at && order.financial_status !== 'refunded'
  const canCancel = isUnfulfilled && isNotCancelled
  const isPaid = ['paid', 'partially_paid'].includes(order.financial_status)
  const orderTotal = parseFloat(order.total_price) || 0

  const customerEmail = fullOrder.customer?.email || fullOrder.email
  const customerName = [fullOrder.customer?.first_name, fullOrder.customer?.last_name].filter(Boolean).join(' ')
  const isShopifyOrder = order.marketplace === 'shopify' || !(order.marketplace)
  const isReverbOrder = order.marketplace === 'reverb'
  const isEbayOrder = order.marketplace === 'ebay'
  const canEmailCustomer = isShopifyOrder && customerEmail
  const isShopifyMarketplace = order.marketplace === 'shopify'
  // A shipped order has nothing left to hold. One already held can always be released.
  const canHold = Boolean(order?.marketplace && order?.order_number) &&
    (Boolean(hold) || (fullOrder.fulfillment_status !== 'fulfilled' && isNotCancelled))
  const canShip = (isReverbOrder || isEbayOrder || isShopifyMarketplace) && fullOrder.fulfillment_status !== 'fulfilled' && !loadingRefunds

  // Any line owned by a seller, not us → the mark-sold prompt renders (D4).
  const hasConsignmentLine = Object.values(itemLocations).some(v => v?.ownership === 'consignment')

  // Already refunded amount (from full order refunds array — Prism flat format)
  const refunds = fullOrder.refunds || []
  const alreadyRefunded = refunds.reduce((sum, r) => sum + (parseFloat(r.amount) || 0), 0)
  const remainingRefundable = orderTotal - alreadyRefunded

  async function handleHoldChange(newHold) {
    setHold(newHold)
    setShipError(null)
    setNotesVersion(v => v + 1)
    await refreshOrderDetail().catch(() => {})
    if (onOrderUpdate) onOrderUpdate()
  }

  // Same refresh after a tracking or an address edit: new note, new detail, list updated.
  async function handleEditSaved() {
    setNotesVersion(v => v + 1)
    await refreshOrderDetail().catch(() => {})
    if (onOrderUpdate) onOrderUpdate()
  }

  async function handleShip(e) {
    e.preventDefault()
    setShipError(null)
    if (!trackingNumber.trim()) return
    if (carriersError || carriers.length === 0) return

    setShipping(true)
    try {
      await api.post(`/api/orders/${order.marketplace}/${order.order_number}/fulfill`, {
        tracking_number: trackingNumber.trim(),
        shipping_provider: shippingProvider,
      })
      // Optimistic flip for instant feedback, then pull server truth so the
      // tracking number/company/URL populate (they live on fullOrder.fulfillments,
      // which the optimistic update alone can't fill).
      setFullOrder(prev => ({ ...prev, fulfillment_status: 'fulfilled' }))
      setShowShipForm(false)
      await refreshOrderDetail().catch(() => {})
      if (onOrderUpdate) onOrderUpdate()
    } catch (err) {
      if (err.message && err.message.toLowerCase().includes('already shipped')) {
        setFullOrder(prev => ({ ...prev, fulfillment_status: 'fulfilled' }))
        setShowShipForm(false)
      }
      // Prism refuses a held order with 409 and says why. Show that text where
      // the staff member is looking, not in a pop-up.
      if (err.status === 409 && /on hold/i.test(err.message)) setShipError(err.message)
      else alert(err.message)
    } finally {
      setShipping(false)
    }
  }

  function openTicketForm() {
    setTicketSubject(`Re: Order ${order.name || '#' + order.order_number}`)
    setTicketMessage('')
    ticketPhotos.reset()
    setShowTicketForm(true)
    setTicketSent(false)
  }

  function closeTicketForm() {
    setShowTicketForm(false)
    setTicketSubject('')
    setTicketMessage('')
    ticketPhotos.reset()
  }

  async function sendTicket() {
    if (!ticketSubject.trim() || !ticketMessage.trim()) {
      alert('Please enter both a subject and message.')
      return
    }

    setSendingTicket(true)
    try {
      const form = new FormData()
      form.append('orderId', String(order.id))
      form.append('orderName', order.name || `#${order.order_number}`)
      form.append('toEmail', customerEmail)
      form.append('toName', customerName)
      form.append('subject', ticketSubject.trim())
      form.append('message', ticketMessage.trim())
      ticketPhotos.files.forEach(file => form.append('files', file))
      await api.postForm('/api/tickets/create-from-order', form)
      setTicketSent(true)
      setTimeout(() => {
        closeTicketForm()
        setTicketSent(false)
      }, 2000)
    } catch (error) {
      alert(`Failed to send email: ${error.message}`)
    } finally {
      setSendingTicket(false)
    }
  }

  return (
    <div className="modal-overlay">
      <div className="modal-content modal-order-detail" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Order Details</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
            <PinButton
              entityType="order"
              entityId={isReverbOrder ? order.id : (order.admin_id || order.id)}
              entityName={`Order #${order.order_number || order.name || order.id}`}
            />
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>

        <div className="modal-body-scroll">
          {canHold && (
            <OrderHoldControl
              marketplace={order.marketplace}
              orderNumber={order.order_number}
              hold={hold}
              onChange={handleHoldChange}
            />
          )}

          {fullOrder.printrun && (
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                background: '#F2EDE4',
                borderLeft: '3px solid #FFD44D',
                padding: '0.5rem 0.75rem',
                margin: '0.5rem 0 1rem',
                fontSize: '0.9em',
              }}
            >
              <span>
                Printed in Printrun #{fullOrder.printrun.id.slice(0, 8)} on{' '}
                {formatDateTime(fullOrder.printrun.created_at)} by{' '}
                {fullOrder.printrun.created_by?.name || '—'}
              </span>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginLeft: 'auto' }}
                onClick={() => {
                  onClose && onClose()
                  navigate(`/orders/printruns?highlight=${fullOrder.printrun.id}`)
                }}
              >
                Open
              </button>
            </div>
          )}

          {/* Warning: fulfillment reverted — remind to cancel shipping label */}
          {canCancel && fullOrder.fulfillments?.length > 0 && (
            <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 6, background: '#fef3c7', border: '1px solid #fde68a', display: 'flex', alignItems: 'flex-start', gap: 8 }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>&#9888;</span>
              <div style={{ fontSize: 12, color: '#92400e', lineHeight: 1.5 }}>
                <strong>Shipping label may still be active.</strong> This order was previously marked as fulfilled. If you cancel, remember to also cancel the shipping label with the carrier to avoid being charged.
              </div>
            </div>
          )}

          {/* Consignment line (D4, revised by spec_20260817): the sale is now
              detected automatically and the payout auto-created behind the
              30-day eligibility gate — the banner points at the book rather
              than prompting a manual mark-sold. */}
          {hasConsignmentLine && (
            <div style={{ padding: '10px 14px', marginBottom: 16, borderRadius: 6, background: '#f3e8ff', border: '1px solid #d8b4fe', display: 'flex', alignItems: 'center', gap: 8 }}>
              <span style={{ fontSize: 16, lineHeight: 1 }}>🤝</span>
              <div style={{ fontSize: 12, color: '#6b21a8', lineHeight: 1.5, flex: 1 }}>
                <strong>Consignment item in this order</strong> — the sale is detected automatically and the seller payout follows the 30-day eligibility window.
              </div>
              <button
                className="btn btn-sm btn-secondary"
                onClick={() => {
                  onClose && onClose()
                  navigate('/deals/consignments')
                }}
              >
                Consignments →
              </button>
            </div>
          )}

          {/* Order Information */}
          <div className="order-detail-section">
            <h3>Order Information</h3>
            <div className="order-detail-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Order ID</span>
                <span className="order-detail-value">{order.admin_id || order.order_number || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Order Number</span>
                <span className="order-detail-value">#{order.order_number || order.name || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Status</span>
                <span className="order-detail-value">
                  <FulfillmentBadge status={fullOrder.fulfillment_status} />
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Financial Status</span>
                <span className="order-detail-value">
                  <FinancialBadge status={order.financial_status} />
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Marketplace</span>
                <span className="order-detail-value">{order.source_name || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Order Date</span>
                <span className="order-detail-value">{formatDateDisplay(order.created_at)}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Updated</span>
                <span className="order-detail-value">{formatDateDisplay(order.updated_at)}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Total</span>
                <span className="order-detail-value order-total">
                  {formatCurrency(order.total_price)}
                  {order.currency && order.currency !== 'GBP' && ` ${order.currency}`}
                </span>
              </div>
            </div>
          </div>

          {/* Customer */}
          <div className="order-detail-section">
            <h3>Customer</h3>
            <div className="order-detail-grid">
              <div className="order-detail-item">
                <span className="order-detail-label">Name</span>
                <span className="order-detail-value">
                  {fullOrder.customer?.first_name || ''} {fullOrder.customer?.last_name || '—'}
                </span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Email</span>
                <span className="order-detail-value">{fullOrder.customer?.email || fullOrder.email || '—'}</span>
              </div>
              <div className="order-detail-item">
                <span className="order-detail-label">Phone</span>
                <span className="order-detail-value">{fullOrder.customer?.phone || fullOrder.shipping_address?.phone || '—'}</span>
              </div>
            </div>
          </div>

          {/* Shipping */}
          <div className="order-detail-section">
            <h3>Shipping</h3>
            <div className="order-detail-grid">
              <OrderTrackingEdit
                marketplace={order.marketplace}
                orderNumber={order.order_number}
                fulfillment={fullOrder.fulfillments?.[0]}
                carriers={carriers}
                canEdit={fullOrder.fulfillment_status === 'fulfilled'}
                onSaved={handleEditSaved}
              />
              <OrderAddressEdit
                marketplace={order.marketplace}
                orderNumber={order.order_number}
                address={fullOrder.shipping_address}
                printrun={fullOrder.printrun}
                canEdit={fullOrder.fulfillment_status !== 'fulfilled'}
                onSaved={handleEditSaved}
              />
            </div>
          </div>

          {/* Line Items */}
          <div className="order-detail-section">
            <h3>Line Items ({fullOrder.line_items?.length || 0})</h3>
            {fullOrder.line_items && fullOrder.line_items.length > 0 ? (
              <table className="table table-compact">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>Reference</th>
                    <th>Warehouse Location</th>
                    <th style={{ textAlign: 'center' }}>Qty</th>
                    <th style={{ textAlign: 'right' }}>Price</th>
                    <th style={{ textAlign: 'right' }}>Total</th>
                  </tr>
                </thead>
                <tbody>
                  {fullOrder.line_items.map((item, idx) => {
                    const productRef = shopifyProductRef(item, order.marketplace)
                    return (
                    <tr key={idx}>
                      <td>
                        {productRef && onProductClick ? (
                          <button
                            className="link-button"
                            onClick={() => onProductClick(productRef)}
                            disabled={loadingProduct}
                          >
                            {loadingProduct ? 'Loading...' : item.title}
                          </button>
                        ) : (
                          item.title
                        )}
                        {item.variant_title && (
                          <span className="text-muted text-sm"> - {item.variant_title}</span>
                        )}
                        {itemLocations[productRef]?.ownership === 'consignment' && (
                          <span style={{ marginLeft: 6 }}>
                            <ConsignmentBadge splitPct={itemLocations[productRef]?.split_pct} />
                          </span>
                        )}
                      </td>
                      <td className="text-muted" style={{ fontFamily: 'monospace', fontSize: 11 }}>
                        {productRef || item.sku || '—'}
                      </td>
                      <td className="text-muted">
                        {itemLocations[productRef]?.shelf || '—'}
                      </td>
                      <td style={{ textAlign: 'center' }}>{item.quantity}</td>
                      <td style={{ textAlign: 'right' }}>{formatCurrency(item.price)}</td>
                      <td style={{ textAlign: 'right' }}>
                        {formatCurrency(parseFloat(item.price) * item.quantity)}
                      </td>
                    </tr>
                    )
                  })}
                  {(() => {
                    const itemsSubtotal = fullOrder.line_items.reduce(
                      (s, it) => s + (parseFloat(it.price) || 0) * (it.quantity || 0), 0
                    )
                    const shippingTotal = (fullOrder.shipping_lines || []).reduce(
                      (s, sh) => s + (parseFloat(sh.price) || 0), 0
                    )
                    const discountTotal = parseFloat(fullOrder.total_discounts) || 0
                    const orderTotal = parseFloat(fullOrder.total_price) || 0
                    const discountLabel = fullOrder.discount_applications?.[0]?.title
                      || fullOrder.discount_codes?.[0]?.code
                      || 'Discount'
                    return (
                      <>
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'right', fontWeight: 500 }}>Subtotal</td>
                          <td style={{ textAlign: 'right' }}>{formatCurrency(itemsSubtotal)}</td>
                        </tr>
                        {shippingTotal > 0 && (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'right' }}>Shipping</td>
                            <td style={{ textAlign: 'right' }}>{formatCurrency(shippingTotal)}</td>
                          </tr>
                        )}
                        {discountTotal > 0 && (
                          <tr>
                            <td colSpan={5} style={{ textAlign: 'right', color: '#dc2626' }}>
                              Discount {discountLabel && <span className="text-muted text-sm">({discountLabel})</span>}
                            </td>
                            <td style={{ textAlign: 'right', color: '#dc2626' }}>
                              −{formatCurrency(discountTotal)}
                            </td>
                          </tr>
                        )}
                        <tr>
                          <td colSpan={5} style={{ textAlign: 'right', fontWeight: 700 }}>Total</td>
                          <td style={{ textAlign: 'right', fontWeight: 700 }}>{formatCurrency(orderTotal)}</td>
                        </tr>
                      </>
                    )
                  })()}
                </tbody>
              </table>
            ) : (
              <p className="text-muted">No line items</p>
            )}
          </div>

          {/* Team notes (Trac #1060) */}
          {order?.marketplace && order?.order_number && (
            <div className="order-detail-section">
              <h3>Notes</h3>
              <OrderNotes key={notesVersion} marketplace={order.marketplace} orderNumber={order.order_number} />
            </div>
          )}

          {/* Refunds */}
          {loadingRefunds ? (
            <div className="order-detail-section">
              <h3>Refunds</h3>
              <p className="text-muted" style={{ fontSize: 12 }}>Loading refund details...</p>
            </div>
          ) : refunds.length > 0 ? (
            <div className="order-detail-section">
              <h3>Refunds ({refunds.length})</h3>
              {refunds.map((refund, idx) => {
                const refundAmount = parseFloat(refund.amount) || 0
                const isPending = refund.status === 'pending'
                const bgColor = isPending ? '#fffbeb' : '#f0fdf4'
                const borderColor = isPending ? '#fde68a' : '#bbf7d0'
                const textColor = isPending ? '#92400e' : '#065f46'
                return (
                  <div key={refund.refund_id || idx} style={{ padding: '8px 12px', background: bgColor, border: `1px solid ${borderColor}`, borderRadius: 6, marginBottom: 6, fontSize: 12 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <span style={{ fontWeight: 600, color: textColor }}>
                        {formatCurrency(refundAmount)}
                        {isPending && <span style={{ fontWeight: 400, marginLeft: 6, fontSize: 11, color: '#b45309' }}>Pending</span>}
                      </span>
                      <span className="text-muted">{formatDateDisplay(refund.created_at)}</span>
                    </div>
                    {refund.note && <div style={{ color: '#6b7280', marginTop: 4 }}>{refund.note}</div>}
                  </div>
                )
              })}
              <div style={{ fontSize: 12, color: '#6b7280', marginTop: 8 }}>
                Total refunded: <strong>{formatCurrency(alreadyRefunded)}</strong> of {formatCurrency(orderTotal)}
                {remainingRefundable > 0 && <> — <strong>{formatCurrency(remainingRefundable)}</strong> remaining</>}
              </div>
            </div>
          ) : null}

          {/* The note on the Shopify order itself, read only. Team notes are the section above. */}
          {fullOrder.note && (
            <div className="order-detail-section">
              <h3>Shopify note</h3>
              <p className="order-note">{fullOrder.note}</p>
            </div>
          )}

          {/* Ship Order Form */}
          {showShipForm && (
            <div className="order-detail-section" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Ship Order</h3>
              {carriersError && (
                <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                  Couldn't load shipping carriers. Refresh the page and try again.
                </div>
              )}
              {shipError && (
                <div role="alert" style={{ marginBottom: 12, padding: '10px 12px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 6, color: '#991b1b', fontSize: 13 }}>
                  {shipError}
                </div>
              )}
              <form onSubmit={handleShip}>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Tracking Number</label>
                  <input
                    type="text"
                    className="form-input"
                    value={trackingNumber}
                    onChange={e => setTrackingNumber(e.target.value)}
                    placeholder="e.g. JD0123456789"
                    disabled={carriersError || carriers.length === 0}
                    required
                    autoFocus
                  />
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Shipping Provider</label>
                  <select
                    className="form-input"
                    value={shippingProvider}
                    onChange={e => setShippingProvider(e.target.value)}
                    disabled={carriersError || carriers.length === 0}
                    required
                  >
                    {carriers.map(p => (
                      <option key={p} value={p}>{p}</option>
                    ))}
                  </select>
                </div>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" className="btn btn-secondary" onClick={() => setShowShipForm(false)} disabled={shipping}>
                    Cancel
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={shipping || !trackingNumber.trim() || carriersError || carriers.length === 0}>
                    {shipping ? 'Shipping...' : 'Ship'}
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Email Customer Form */}
          {showTicketForm && (
            <div className="order-detail-section" style={{ background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: 16 }}>
              <h3 style={{ marginTop: 0 }}>Email Customer</h3>
              {ticketSent ? (
                <div style={{ padding: '12px 16px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6, color: '#166534', fontWeight: 500 }}>
                  Email sent and ticket created successfully.
                </div>
              ) : (
                <>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>To</label>
                    <input
                      type="text"
                      className="form-input"
                      value={customerEmail}
                      disabled
                      style={{ background: '#f1f5f9' }}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Subject</label>
                    <input
                      type="text"
                      className="form-input"
                      value={ticketSubject}
                      onChange={e => setTicketSubject(e.target.value)}
                    />
                  </div>
                  <div style={{ marginBottom: 12 }}>
                    <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#64748b', marginBottom: 4 }}>Message</label>
                    <textarea
                      className="form-input"
                      rows={6}
                      value={ticketMessage}
                      onChange={e => setTicketMessage(e.target.value)}
                      placeholder="Write your message to the customer..."
                      style={{ minHeight: 120, resize: 'vertical' }}
                      autoFocus
                    />
                    <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                      <OutboundPhotoPicker photos={ticketPhotos} />
                    </div>
                    <OutboundPhotoList photos={ticketPhotos} />
                  </div>
                  <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                    <button className="btn btn-secondary" onClick={closeTicketForm} disabled={sendingTicket}>
                      Cancel
                    </button>
                    <button className="btn btn-primary" onClick={sendTicket} disabled={sendingTicket || ticketPhotos.preparing}>
                      {sendingTicket ? 'Sending...' : 'Send Email'}
                    </button>
                  </div>
                  <p style={{ fontSize: 11, color: '#94a3b8', marginTop: 8, marginBottom: 0 }}>
                    This will send an email to the customer and create a ticket in the ticket system.
                  </p>
                </>
              )}
            </div>
          )}

        </div>

        {canShip && !showShipForm && !fullOrder.printrun && (
          <div style={{
            padding: '0.25rem var(--space-6) 0',
            fontSize: '0.75rem',
            color: '#92400e',
            textAlign: 'right',
          }}>
            Add this order to a printrun before shipping.
          </div>
        )}
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-secondary" onClick={() => generateVatInvoicePdf(fullOrder)}>
            Print VAT Invoice
          </button>
          {canShip && !showShipForm && (
            <button
              className="btn btn-primary"
              onClick={() => setShowShipForm(true)}
              disabled={!fullOrder.printrun || Boolean(hold)}
              style={hold ? { opacity: 0.5, cursor: 'not-allowed' } : undefined}
              title={hold
                ? `On hold: ${holdReasonLabel(hold.reason)}. Release the hold before shipping.`
                : (!fullOrder.printrun ? 'Add this order to a printrun before shipping.' : undefined)}
            >
              {hold ? 'Ship (on hold)' : 'Ship'}
            </button>
          )}
          {canEmailCustomer && !showTicketForm && (
            <button className="btn btn-moreinfo" onClick={openTicketForm}>
              Email Customer
            </button>
          )}
          {order.admin_id && !isReverbOrder && !isEbayOrder && (
            <a
              href={`${SHOPIFY_ADMIN_URL}/orders/${order.admin_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              View in Shopify
            </a>
          )}
        </div>
      </div>
    </div>
  )
}

export default OrderDetailModal
